import {Button} from '@/components/ui/button'
import {cn} from '@/lib/utils'

type SegmentedToggleTone = 'success' | 'destructive' | 'neutral'
type SegmentedToggleSize = 'sm' | 'md'

export interface SegmentedToggleOption<T extends string> {
    value: T
    label: string
    tone: SegmentedToggleTone
    testId?: string
}

interface SegmentedToggleProps<T extends string> {
    options: ReadonlyArray<SegmentedToggleOption<T>>
    value: T
    onChange: (value: T) => void
    size?: SegmentedToggleSize
    disabled?: boolean
    'data-testid'?: string
}

/* eslint-disable i18next/no-literal-string -- CSS class constants */
const sizeClasses: Record<SegmentedToggleSize, string> = {
    sm: 'px-3 h-8',
    md: 'px-4 h-9',
}
const activeToneClasses: Record<SegmentedToggleTone, string> = {
    success: 'bg-success/10 text-success hover:bg-success/20',
    destructive: 'bg-destructive/10 text-destructive hover:bg-destructive/20',
    neutral: 'bg-muted text-muted-foreground',
}
/* eslint-enable i18next/no-literal-string */

/* eslint-disable i18next/no-literal-string -- CSS class constants */
function segmentShape(index: number, count: number): string {
    if (count === 1) return 'rounded-md'
    if (index === 0) return 'rounded-none rounded-l-md border-r'
    if (index === count - 1) return 'rounded-none rounded-r-md'
    return 'rounded-none border-r'
}
/* eslint-enable i18next/no-literal-string */

export function SegmentedToggle<T extends string>({
    options,
    value,
    onChange,
    size = 'sm',
    disabled,
    'data-testid': testId,
}: SegmentedToggleProps<T>) {
    return (
        <div className="inline-flex rounded-md border" data-testid={testId}>
            {options.map((option, index) => {
                const isActive = option.value === value
                return (
                    <Button
                        key={option.value}
                        size="sm"
                        variant="ghost"
                        className={cn(
                            segmentShape(index, options.length),
                            sizeClasses[size],
                            isActive && activeToneClasses[option.tone],
                        )}
                        onClick={() => onChange(option.value)}
                        disabled={disabled}
                        data-testid={option.testId}
                    >
                        {option.label}
                    </Button>
                )
            })}
        </div>
    )
}
